import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import ReviewsTable from './ReviewsTable';
import RatingsTable from './RatingsTable';
import BigNumbersTable from './BigNumbersTable';
import StudyCasesTable from './StudyCasesTable';
import { ScrapeAnalyzeSection, ScrapeReviewPanel, ResearchHistoryDialog } from '@modules/product/components';
import { SAccordion } from '@modules/ui-components/dialogs/SAccordion';
import { useAuth } from '../../../../frontend/src/contexts/AuthContext';
import { useTaskContext } from '../../../../frontend/src/contexts/TaskContext';

interface ProofSectionProps {
  project: Record<string, unknown> | null;
  projectId: string;
  fetchProjectData?: () => void;
}

export default function ProofSection({ project, projectId, fetchProjectData }: ProofSectionProps) {
  const [error, setError] = useState('');
  const [reviewAnalysisId, setReviewAnalysisId] = useState<string | null>(null);
  const [showResearchHistory, setShowResearchHistory] = useState(false);
  const { currentUser } = useAuth();
  const {
    setCurrentProjectId,
    registerImmediateRefreshCallback,
    unregisterImmediateRefreshCallback,
  } = useTaskContext();

  useEffect(() => {
    if (projectId) {
      setCurrentProjectId(projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      const callbackKey = `product-description-${projectId}`;
      const refreshCallback = () => {
        if (fetchProjectData) {
          fetchProjectData();
        }
      };

      registerImmediateRefreshCallback(callbackKey, refreshCallback);

      return () => {
        unregisterImmediateRefreshCallback(callbackKey);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>;
    if (error) {
      timerId = setTimeout(() => {
        setError('');
      }, 7000);
    }
    return () => clearTimeout(timerId);
  }, [error]);

  if (!project) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: { xs: 1, sm: 2, md: 3 }, mt: 0 }} data-testid="proof-section">
      {/* AI Product Research — restricted to Social Proofs extraction */}
      <SAccordion title="AI Product Research" collapsible defaultCollapsed sx={{ mb: 3 }}>
        {reviewAnalysisId ? (
          <ScrapeReviewPanel
            projectId={projectId}
            userId={currentUser?.uid || ''}
            analysisId={reviewAnalysisId}
            onApplied={() => {
              setReviewAnalysisId(null);
              fetchProjectData?.();
            }}
            onClose={() => setReviewAnalysisId(null)}
          />
        ) : (
          <ScrapeAnalyzeSection
            projectId={projectId}
            userId={currentUser?.uid || ''}
            allowedGoals={['socialProofs']}
            onComplete={() => fetchProjectData?.()}
            onReviewReady={(analysisId) => setReviewAnalysisId(analysisId)}
            onOpenHistory={() => setShowResearchHistory(true)}
          />
        )}
      </SAccordion>

      <ReviewsTable projectId={projectId} />
      <RatingsTable projectId={projectId} />
      <BigNumbersTable projectId={projectId} />
      <StudyCasesTable projectId={projectId} />

      {/* Research History Dialog */}
      <ResearchHistoryDialog
        open={showResearchHistory}
        onClose={() => setShowResearchHistory(false)}
        projectId={projectId}
        userId={currentUser?.uid || ''}
        onApplied={() => { setShowResearchHistory(false); fetchProjectData?.(); }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
    </Paper>
  );
}
